import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import AppService from '../services/app';
import { useAuth } from './AuthContext';

const AppContext = createContext();

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const [apps, setApps] = useState([]);
  const [currentApp, setCurrentApp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();
  
  // Use refs to track if we've already fetched apps and prevent multiple fetches
  const hasFetchedRef = useRef(false);
  const isFetchingRef = useRef(false);

  // Reset state when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setApps([]);
      setCurrentApp(null);
      setError(null);
      hasFetchedRef.current = false;
      isFetchingRef.current = false;
    }
  }, [isAuthenticated]);

  // Function to fetch apps
  const fetchApps = React.useCallback(() => {
    // Don't fetch if not authenticated, already fetching, or already fetched with data
    if (!isAuthenticated || isFetchingRef.current || (hasFetchedRef.current && apps.length > 0)) {
      return Promise.resolve();
    }
    
    isFetchingRef.current = true;
    setLoading(true);
    
    return AppService.getApps()
      .then(response => {
        const appsList = response.apps || [];
        setApps(appsList);
        
        // Set the current app to the first app in the list if there is one and no app is currently selected
        if (appsList.length > 0 && !currentApp) {
          const savedAppId = localStorage.getItem('currentAppId');
          if (savedAppId) {
            const savedApp = appsList.find(app => app.id.toString() === savedAppId);
            if (savedApp) {
              setCurrentApp(savedApp);
            } else {
              setCurrentApp(appsList[0]);
              localStorage.setItem('currentAppId', appsList[0].id.toString());
            }
          } else {
            setCurrentApp(appsList[0]);
            localStorage.setItem('currentAppId', appsList[0].id.toString());
          }
        }
        
        setError(null);
        hasFetchedRef.current = true;
        return appsList;
      })
      .catch(err => {
        setError('Failed to load apps: ' + (err.response?.data || err.message));
        throw err;
      })
      .finally(() => {
        setLoading(false);
        isFetchingRef.current = false;
      });
  }, [isAuthenticated, apps.length, currentApp]);

  // Fetch apps once when authenticated
  useEffect(() => {
    if (isAuthenticated && !hasFetchedRef.current) {
      fetchApps();
    }
  }, [isAuthenticated, fetchApps]);

  const switchApp = (app) => {
    setCurrentApp(app);
    localStorage.setItem('currentAppId', app.id.toString());
  };

  const addApp = (app) => {
    setApps(prevApps => [...prevApps, app]);
    
    // If this is the first app, set it as the current app
    if (apps.length === 0) {
      setCurrentApp(app);
      localStorage.setItem('currentAppId', app.id.toString());
    }
  };

  const removeApp = (appId) => {
    const updatedApps = apps.filter(app => app.id !== appId);
    setApps(updatedApps);
    
    // If the removed app was the current app, switch to the first app in the list
    if (currentApp && currentApp.id === appId) {
      if (updatedApps.length > 0) {
        setCurrentApp(updatedApps[0]);
        localStorage.setItem('currentAppId', updatedApps[0].id.toString());
      } else {
        setCurrentApp(null);
        localStorage.removeItem('currentAppId');
      }
    }
  };

  // Create a value object that doesn't change on every render
  const contextValue = React.useMemo(() => ({
    apps,
    currentApp,
    loading,
    error,
    fetchApps,
    switchApp,
    addApp,
    removeApp
  }), [apps, currentApp, loading, error, fetchApps]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;